import { Divider, Grid, Typography } from "@mui/material";
import {
  AutoComplete,
  FormInput,
  ISelectType,
  useFetch,
  useFormContext,
} from "@4uhub/lib4uhub";
import { useTranslation } from "react-i18next";
import { DonorAddresses } from "./DonorAddresses/DonorAddresses";
import { DonorContacts } from "./DonorContacts/DonorContacts";
import { listMainGenerics } from "../../../../services/generic.service";
import { useWatch } from "react-hook-form";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import UsersService from "../../../../services/users.service";
import { TDonorsForm } from "./donorsSchema";
import { PersonalData } from "./PersonalData/PersonalData";

const translationPath = "page.4uDonation.donors.form.";

const usersService = new UsersService();

export const DonorsFields = memo(() => {
  const { t } = useTranslation();

  const [users, setUsers] = useState<ISelectType[]>([]);

  const donorTypeIdentifier = useMemo(() => ({ identifier: "DonorType" }), []);
  const userIntegrationTypeIdentifier = useMemo(
    () => ({ identifier: "userIntegrationType" }),
    []
  );

  const { sendRequest } = useFetch(usersService.listUsers);
  const { control, setValue } = useFormContext<TDonorsForm>();

  const donorType = useWatch({ control, name: "donorType" });
  const attendant = useWatch({ control, name: "attendant" });
  const attendantSecondary = useWatch({ control, name: "attendantSecondary" });

  const prevDonorTypeIdRef = useRef<string | undefined>(undefined);
  const cleanIndividual = useCallback(() => {
    setValue("individual", null);
    setValue("legalEntity", null);
  }, [setValue]);

  useEffect(() => {
    if (prevDonorTypeIdRef.current === undefined) {
      prevDonorTypeIdRef.current = donorType?.id;
      return;
    }
    if (prevDonorTypeIdRef.current !== donorType?.id) {
      cleanIndividual();
      prevDonorTypeIdRef.current = donorType?.id;
    }
  }, [donorType, cleanIndividual]);

  const loadFilteredUsers = useCallback(async () => {
    const { data, success } = await sendRequest(undefined);
    if (data && success) {
      setUsers(data.map((user) => ({ id: user.id, name: user.fullName })));
    }
  }, [sendRequest]);

  useEffect(() => {
    loadFilteredUsers();
  }, [loadFilteredUsers]);

  const attendantOptions = useMemo(
    () => users.filter((user) => user.id !== attendantSecondary?.id),
    [attendantSecondary, users]
  );

  const attendantSecondaryOptions = useMemo(
    () => users.filter((user) => user.id !== attendant?.id),
    [attendant, users]
  );

  return (
    <Grid container spacing={2}>
      {/* --- Dados Básicos --- */}
      <Grid item xs={12} sm={12}>
        <AutoComplete
          name="donorType"
          params={donorTypeIdentifier}
          request={listMainGenerics}
          label={t(translationPath + "donor_type") || "Entidade Doadora"}
          getOptionLabel={(option) => option.name}
          fullWidth
          size="small"
        />
      </Grid>

      <PersonalData />

      <Grid item xs={12} sm={12}>
        <FormInput
          name="integrationId"
          label={t(translationPath + "integration_id")}
          fullWidth
          size="small"
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <AutoComplete
          name="attendant"
          label={t(translationPath + "attendant")}
          getOptionLabel={(option) => option.name}
          size="small"
          options={attendantOptions}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <AutoComplete
          name="attendantSecondary"
          label={t(translationPath + "attendant_secondary")}
          getOptionLabel={(option) => option.name}
          size="small"
          options={attendantSecondaryOptions}
        />
      </Grid>

      <Grid item xs={12} sm={12}>
        <AutoComplete
          name="userIntegrationType"
          request={listMainGenerics}
          params={userIntegrationTypeIdentifier}
          label={
            t(translationPath + "user_integration_type") ||
            "Tipo de Integração de Usuário"
          }
          getOptionLabel={(option) => option.name}
          fullWidth
          size="small"
        />
      </Grid>

      {/* --- Contatos --- */}
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>
          {t(translationPath + "contacts") || "Contatos"}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <DonorContacts />
      </Grid>

      {/* --- Endereços --- */}
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
      </Grid>
      <Grid item xs={12}>
        <DonorAddresses />
      </Grid>
    </Grid>
  );
});
